import { Switch } from "antd";
import { Flag, Loader2, Plus } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { actorFromAuth } from "src/lib/activityService";
import {
  createFeatureFlag,
  deleteFeatureFlag,
  getAllFeatureFlags,
  toggleFeatureFlag,
  updateFeatureFlag,
  validateFeatureFlag,
  type FeatureFlag,
} from "src/lib/featureFlagsService";
import { canReadService, canWriteService, ServiceDomain } from "src/lib/roles";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import Collapsible from "../components/Collapsible";
import CreateFlagModal from "../components/CreateFlagModal";
import {
  FeatureFlagCard,
  type FlagWithKey,
} from "../components/featureFlags/FeatureFlagCard";
import { SlabButton } from "../components/ui/SlabButton";

const nunito = { fontFamily: "'Nunito', system-ui, sans-serif" } as const;

const FeatureFlagsPanel: React.FC = () => {
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const activityActor = actorFromAuth(user, userProfile);
  const canChangeSettings =
    userProfile &&
    canWriteService(userProfile.role, ServiceDomain.FeatureFlags);
  const [flags, setFlags] = useState<FlagWithKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingFlag, setUpdatingFlag] = useState<string | null>(null);
  const [editingFlag, setEditingFlag] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<FeatureFlag>>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFlagKey, setNewFlagKey] = useState("");
  const [newFlagData, setNewFlagData] = useState<FeatureFlag>({
    enabled: false,
    description: "",
    environment: [],
  });

  useEffect(() => {
    loadFlags();
  }, []);

  const loadFlags = async () => {
    try {
      setLoading(true);
      setError(null);
      const flagsData = await getAllFeatureFlags();

      // Convert to array with keys
      const flagsArray: FlagWithKey[] = Object.entries(flagsData).map(
        ([key, flag]) => ({
          key,
          ...flag,
        }),
      );

      // Sort by key name
      flagsArray.sort((a, b) => a.key.localeCompare(b.key));

      setFlags(flagsArray);
    } catch (err: any) {
      setError(err.message || "Failed to load feature flags");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (flagKey: string) => {
    if (!canChangeSettings) return;
    try {
      setUpdatingFlag(flagKey);
      const newStatus = await toggleFeatureFlag(
        flagKey,
        activityActor ?? undefined,
      );

      // Update local state
      setFlags(
        flags.map((flag) =>
          flag.key === flagKey ? { ...flag, enabled: newStatus } : flag,
        ),
      );
    } catch (err: any) {
      alert(`Failed to toggle flag: ${err.message}`);
    } finally {
      setUpdatingFlag(null);
    }
  };

  const handleEdit = (flag: FlagWithKey) => {
    if (!canChangeSettings) return;
    setEditingFlag(flag.key);
    setEditForm({
      enabled: flag.enabled,
      min_build: flag.min_build,
      platforms: flag.platforms,
      description: flag.description,
      environment: flag.environment,
    });
  };

  const handleSaveEdit = async (flagKey: string) => {
    if (!canChangeSettings) return;
    try {
      // Validate
      const errors = validateFeatureFlag(editForm);
      if (errors.length > 0) {
        alert(`Validation errors:\n${errors.join("\n")}`);
        return;
      }

      setUpdatingFlag(flagKey);
      await updateFeatureFlag(flagKey, editForm, activityActor ?? undefined);

      // Update local state
      setFlags(
        flags.map((flag) =>
          flag.key === flagKey ? { ...flag, ...editForm } : flag,
        ),
      );
      setEditingFlag(null);
      setEditForm({});
    } catch (err: any) {
      alert(`Failed to update flag: ${err.message}`);
    } finally {
      setUpdatingFlag(null);
    }
  };

  const handleDelete = async (flagKey: string) => {
    if (!canChangeSettings) return;
    if (!window.confirm(`Delete "${flagKey}"? This cannot be undone.`)) return;
    try {
      setUpdatingFlag(flagKey);
      await deleteFeatureFlag(flagKey, activityActor ?? undefined);

      // Remove from local state
      setFlags(flags.filter((flag) => flag.key !== flagKey));
    } catch (err: any) {
      alert(`Failed to delete flag: ${err.message}`);
    } finally {
      setUpdatingFlag(null);
    }
  };

  const handleCreateFlag = async () => {
    if (!canChangeSettings) return;
    try {
      if (!newFlagKey.trim()) {
        alert("Flag key is required");
        return;
      }

      // Validate key format (alphanumeric and underscores only)
      if (!/^[a-z0-9_]+$/.test(newFlagKey)) {
        alert(
          "Flag key must contain only lowercase letters, numbers, and underscores",
        );
        return;
      }

      // Check if key already exists
      if (flags.some((f) => f.key === newFlagKey)) {
        alert("A flag with this key already exists");
        return;
      }

      // Validate flag data
      const errors = validateFeatureFlag(newFlagData);
      if (errors.length > 0) {
        alert(`Validation errors:\n${errors.join("\n")}`);
        return;
      }

      setUpdatingFlag(newFlagKey);
      await createFeatureFlag(
        newFlagKey,
        newFlagData,
        activityActor ?? undefined,
      );

      // Add to local state
      setFlags(
        [...flags, { key: newFlagKey, ...newFlagData }].sort((a, b) =>
          a.key.localeCompare(b.key),
        ),
      );

      // Reset form
      setShowCreateModal(false);
      setNewFlagKey("");
      setNewFlagData({ enabled: false, description: "", environment: [] });
    } catch (err: any) {
      alert(`Failed to create flag: ${err.message}`);
    } finally {
      setUpdatingFlag(null);
    }
  };

  if (
    !userProfile ||
    !canReadService(userProfile.role, ServiceDomain.FeatureFlags)
  ) {
    return (
      <div
        className="mx-auto max-w-lg rounded-[2rem] border-4 border-[#E5E5E5] bg-white p-8 text-center shadow-[0_8px_0_#E5E5E5] sm:p-10"
        style={nunito}
      >
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FFEDED] text-3xl">
          🔒
        </div>
        <h1 className="mb-2 text-2xl font-extrabold text-[#3C3C3C] sm:text-3xl">
          Access denied
        </h1>
        <p className="text-base font-semibold text-[#777777]">
          You don&apos;t have permission to view feature flags.
        </p>
      </div>
    );
  }

  return (
    <>
      <section
        className="mx-auto flex h-full min-h-0 w-full max-w-4xl flex-col gap-4"
        style={nunito}
      >
        <header className="shrink-0 rounded-[1.5rem] border border-white/10 bg-gray-950/45 p-4 shadow-[0_6px_0_rgba(0,0,0,0.25)] backdrop-blur-sm sm:p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-[#EEF2FF] px-3 py-1 text-xs font-extrabold text-[#4338CA] ring-2 ring-[#6366F1]/30">
                <Flag className="h-3.5 w-3.5" aria-hidden />
                Feature flags
              </div>

              <h1 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                Roll out safely
              </h1>

              <p className="mt-1 text-sm font-semibold text-white/65">
                Toggle and scope flags by platform and environment.
              </p>
              <p className="mt-1 text-sm font-semibold text-white/65">
                Note: Changes take up to 5 minutes to take effect.
              </p>
            </div>

            {canChangeSettings && (
              <SlabButton
                variant="primary"
                type="button"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="h-5 w-5" aria-hidden />
                New flag
              </SlabButton>
            )}
          </div>
        </header>

        {error && (
          <div
            className="shrink-0 rounded-2xl border-2 border-[#EF4444] bg-[#FEF2F2] p-3 shadow-[0_4px_0_#FECACA]"
            role="alert"
          >
            <p className="text-center text-sm font-extrabold text-[#B91C1C]">
              {error}
            </p>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-hidden rounded-[1.5rem] border border-white/10 bg-gray-950/35 shadow-[0_8px_0_rgba(0,0,0,0.3)]">
          {loading ? (
            <div className="flex h-full min-h-[18rem] flex-col items-center justify-center py-12">
              <Loader2
                className="h-10 w-10 animate-spin text-[#14B8A6]"
                aria-label="Loading"
              />
              <p className="mt-4 font-extrabold text-white/70">
                Loading flags…
              </p>
            </div>
          ) : flags.length === 0 ? (
            <div className="flex h-full min-h-[18rem] items-center justify-center p-6">
              <div className="rounded-[1.5rem] border-4 border-dashed border-white/25 bg-white/5 p-8 text-center">
                <p className="text-base font-extrabold text-white/90 sm:text-lg">
                  {canChangeSettings
                    ? "No flags yet — create one to start rolling out."
                    : "No feature flags configured."}
                </p>
              </div>
            </div>
          ) : (
            <div className="h-full overflow-y-auto overscroll-contain p-3 sm:p-4">
              <ul className="flex flex-col gap-2.5">
                {flags.map((flag) => (
                  <li key={flag.key}>
                    <Collapsible
                      header={
                        <div className="pl-4 flex flex-col items-start justify-between gap-2">
                          <p className="font-mono text-sm font-extrabold leading-snug text-[#3C3C3C] break-all sm:text-base">
                            {flag.key}
                          </p>
                          {!(editingFlag === flag.key) && flag.description && (
                            <p className="mt-0.5 line-clamp-2 text-xs font-semibold leading-snug text-[#777777] sm:text-sm">
                              {flag.description}
                            </p>
                          )}
                        </div>
                      }
                      trailing={
                        <Switch
                          checked={
                            editingFlag === flag.key
                              ? (editForm.enabled ?? flag.enabled)
                              : flag.enabled
                          }
                          onChange={(next) => {
                            if (editingFlag === flag.key) {
                              setEditForm({ ...editForm, enabled: next });
                            } else {
                              handleToggle(flag.key);
                            }
                          }}
                          disabled={
                            updatingFlag === flag.key || !canChangeSettings
                          }
                          className="focus-visible:ring-[#6366F1]"
                          aria-label="Toggle flag enabled"
                        />
                      }
                      leadingChevron={true}
                    >
                      <FeatureFlagCard
                        flag={flag}
                        editing={editingFlag === flag.key}
                        editForm={editForm}
                        canChangeSettings={!!canChangeSettings}
                        busy={updatingFlag === flag.key}
                        onToggle={() => handleToggle(flag.key)}
                        onEdit={() => handleEdit(flag)}
                        onSave={() => handleSaveEdit(flag.key)}
                        onCancel={() => {
                          setEditingFlag(null);
                          setEditForm({});
                        }}
                        onDelete={() => handleDelete(flag.key)}
                        setEditForm={setEditForm}
                      />
                    </Collapsible>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      <CreateFlagModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateFlag}
        newFlagKey={newFlagKey}
        setNewFlagKey={setNewFlagKey}
        newFlagData={newFlagData}
        setNewFlagData={setNewFlagData}
      />
    </>
  );
};

export default FeatureFlagsPanel;
